import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSystemMemoryUtilisation } from "../../redux/actions/dashboard";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";
export default function MemoryUtilization() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sortDirection, setSortDirection] = useState("asc");
  const [durationFilter, setDurationFilter] = useState("Last 24 Hours");
  const [searchTerm, setSearchTerm] = useState("");

  const { loading, data } = useSelector(
    (state) => state.dashboard.getMonitorHealthDashboard
  );

  useEffect(() => {
    dispatch(getSystemMemoryUtilisation(durationFilter));
  }, [dispatch, durationFilter]);

  const now = dayjs();
  const rounded = now.startOf("hour");
  const memoryData = Array.isArray(data?.memory_log?.data)
    ? data.memory_log.data
    : [];

  const latestTimestamp =
    memoryData.length > 0
      ? Math.max(...memoryData.map((item) => item.last_updated))
      : null;

  const durationMapping = {
    "Last 1 Hour": 1 * 60 * 60 * 1000,
    "Last 6 Hours": 6 * 60 * 60 * 1000,
    "Last 12 Hours": 12 * 60 * 60 * 1000,
    "Last 24 Hours": 24 * 60 * 60 * 1000,
  };

  const filteredData = memoryData
    .filter((row) => {
      if (!row?.last_updated) return false;

      const rowTime = row.last_updated * 1000;
      const referenceTime = latestTimestamp
        ? latestTimestamp * 1000
        : Date.now();

      const selectedDuration =
        durationMapping[durationFilter] || durationMapping["Last 24 Hours"];

      const matchesDuration = rowTime >= referenceTime - selectedDuration;

      const matchesSearch =
        searchTerm === "" ||
        String(row.memory_utilisation)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesDuration && matchesSearch;
    })
    .sort((a, b) => {
      const tA = dayjs.unix(a.last_updated);
      const tB = dayjs.unix(b.last_updated);
      return sortDirection === "asc"
        ? tA.isBefore(tB)
          ? -1
          : 1
        : tA.isAfter(tB)
        ? -1
        : 1;
    });

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: "40px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "14px",
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Box display="flex" gap={2}>
            <FormControl
              size="small"
              variant="outlined"
              sx={{
                width: "200px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root fieldset": { borderColor: "#BDBDBD" },
              }}
            >
              <InputLabel>Duration</InputLabel>
              <Select
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
                label="Duration"
              >
                <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
                <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
                <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "270px",
                height: "42px",
                "& .MuiOutlinedInput-input": {
                  padding: "10px 14px",
                },
              }}
              InputProps={{
                startAdornment: <Search sx={{ color: "#888", mr: 1 }} />,
              }}
            />
          </Box>

          {/* Back Button */}
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Typography variant="h6" fontWeight={600} mb={2}>
          Memory Utilization
        </Typography>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #ddd",
            width: "100%",
          }}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                  {[
                    "Server Name",
                    "Memory Used (%)",
                    "Total Memory",
                    "Used (GB)",
                    "Free (GB)",
                    "Last Updated",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{ fontWeight: 600, px: 3, py: 2 }}
                    >
                      {header === "Last Updated" ? (
                        <Box display="flex" alignItems="center">
                          <span
                            style={{
                              display: "inline-block",
                            }}
                          >
                            {header}
                          </span>
                          <Box
                            display="flex"
                            flexDirection="column"
                            sx={{ ml: 1, display: "inline-flex" }}
                          >
                            <Box
                              onClick={() => setSortDirection("asc")}
                              sx={{
                                background: "rgb(225, 225, 225)",
                                border: "1px solid #ccc",
                                borderRadius: "2px",
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                                mb: "1px",
                              }}
                            >
                              <ArrowDropUpIcon
                                sx={{
                                  fontSize: 16,
                                  color:
                                    sortDirection === "asc"
                                      ? "#7B2D43"
                                      : "#999",
                                }}
                              />
                            </Box>
                            <Box
                              onClick={() => setSortDirection("desc")}
                              sx={{
                                background: "rgb(225, 225, 225)",
                                border: "1px solid #ccc",
                                borderRadius: "2px",
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                              }}
                            >
                              <ArrowDropDownIcon
                                sx={{
                                  fontSize: 16,
                                  color:
                                    sortDirection === "desc"
                                      ? "#7B2D43"
                                      : "#999",
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        header
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        "& td": { py: 2.5, borderBottom: "1px solid #ddd" },
                      }}
                    >
                      <TableCell>{row.server_name}</TableCell>
                      <TableCell>{row.memory_utilisation} %</TableCell>
                      <TableCell>{row.total_memory} GB</TableCell>
                      <TableCell>{row.used_memory} GB</TableCell>
                      <TableCell>{row.free_memory} GB</TableCell>
                      <TableCell>
                        {dayjs
                          .unix(row.last_updated)
                          .format("DD/MM/YYYY, hh:mm A")}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
}
